import React, { useState } from 'react';
import { db, storage } from '../firebase/config';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import * as XLSX from 'xlsx';
import {
    FileSpreadsheet, Download, Upload, Images, Eye, CheckCircle,
    XCircle, Copy, AlertTriangle, ChevronRight, ChevronDown, Trash2, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BulkProductUpload = ({ userInfo }) => {
    const navigate = useNavigate();

    // 단계 관리
    const [currentStep, setCurrentStep] = useState(1);

    // 이미지 업로드 상태
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);

    // 엑셀 업로드 상태
    const [excelData, setExcelData] = useState([]);
    const [uploadingBulk, setUploadingBulk] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // 미리보기 확장/축소
    const [expandedRows, setExpandedRows] = useState({});

    const categories = [
        { value: 'implant', label: '임플란트' },
        { value: 'restorative', label: '수복재료' },
        { value: 'endodontic', label: '근관치료' },
        { value: 'preventive', label: '예방치료' },
        { value: 'impression', label: '인상재료' },
        { value: 'orthodontic', label: '교정재료' },
        { value: 'surgical', label: '외과용품' },
        { value: 'hygiene', label: '위생용품' },
        { value: 'equipment', label: '장비' },
        { value: 'lab-material', label: '기공재료' },
        { value: 'other', label: '기타' }
    ];

    const steps = [
        { num: 1, title: '템플릿 다운로드', desc: '엑셀 양식을 받으세요' },
        { num: 2, title: '이미지 업로드', desc: '상품 이미지를 올리세요 (선택)' },
        { num: 3, title: '엑셀 업로드', desc: '작성한 엑셀을 올리세요' },
        { num: 4, title: '미리보기/검증', desc: '등록 전 확인하세요' },
        { num: 5, title: '일괄 등록', desc: '상품을 등록하세요' },
    ];

    // ── Step 1: 엑셀 템플릿 다운로드 ──
    const downloadExcelTemplate = () => {
        const templateData = [
            {
                '상품명': '오스템 TS III SA 임플란트 픽스처',
                '브랜드': 'Osstem',
                '카테고리': 'implant',
                '세부카테고리': 'implant-fixture',
                '상품설명': '탁월한 초기 고정력과 골유착률을 자랑하는 프리미엄 임플란트 픽스처',
                '판매가': 85000,
                '정가': 100000,
                '재고': 100,
                '단위': 'EA',
                '최소주문수량': 1,
                '제조사': '오스템임플란트',
                '원산지': '대한민국',
                '제품사양': '직경: 4.0mm, 길이: 10mm, 재질: 순수 티타늄 Grade 4',
                '주요특징': 'SA 표면처리, 우수한 초기 안정성, 빠른 골유착',
                '인증정보': 'FDA, CE, KFDA, ISO 13485',
                '배송정보': '무료배송',
                '반품정책': '7일 이내 반품 가능',
                '옵션1명': '직경',
                '옵션1값': '3.5mm,4.0mm,4.5mm,5.0mm',
                '옵션1추가금액': '0,0,5000,10000',
                '옵션2명': '길이',
                '옵션2값': '8.5mm,10mm,11.5mm,13mm',
                '옵션2추가금액': '0,0,0,0',
                '옵션3명': '',
                '옵션3값': '',
                '옵션3추가금액': '',
                '이미지URL1': '',
                '이미지URL2': '',
                '이미지URL3': '',
                '이미지URL4': '',
                '이미지URL5': ''
            },
            {
                '상품명': '3M ESPE Filtek Z350 XT 컴포짓 레진',
                '브랜드': '3M',
                '카테고리': 'restorative',
                '세부카테고리': 'restorative-composite',
                '상품설명': '우수한 심미성과 내구성을 갖춘 나노필러 유니버설 컴포짓 레진',
                '판매가': 45000,
                '정가': 55000,
                '재고': 200,
                '단위': 'SYRINGE',
                '최소주문수량': 1,
                '제조사': '3M',
                '원산지': '미국',
                '제품사양': '용량: 4g, 타입: 시린지',
                '주요특징': '나노필러 기술, 뛰어난 연마성, 자연스러운 형광',
                '인증정보': 'FDA, CE',
                '배송정보': '무료배송',
                '반품정책': '개봉 전 반품 가능',
                '옵션1명': '쉐이드',
                '옵션1값': 'A1,A2,A3,A3.5,B1,B2',
                '옵션1추가금액': '0,0,0,0,0,0',
                '옵션2명': '타입',
                '옵션2값': '시린지,캡슐',
                '옵션2추가금액': '0,3000',
                '옵션3명': '용량',
                '옵션3값': '2g,4g',
                '옵션3추가금액': '0,5000',
                '이미지URL1': '',
                '이미지URL2': '',
                '이미지URL3': '',
                '이미지URL4': '',
                '이미지URL5': ''
            }
        ];

        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 50 },
            { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
            { wch: 18 }, { wch: 12 }, { wch: 50 }, { wch: 50 }, { wch: 25 },
            { wch: 15 }, { wch: 20 },
            { wch: 15 }, { wch: 40 }, { wch: 30 },
            { wch: 15 }, { wch: 40 }, { wch: 30 },
            { wch: 15 }, { wch: 40 }, { wch: 30 },
            { wch: 60 }, { wch: 60 }, { wch: 60 }, { wch: 60 }, { wch: 60 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '상품목록');

        // 카테고리 시트
        const categorySheet = XLSX.utils.json_to_sheet([
            { '메인카테고리': 'implant', '설명': '임플란트', '세부카테고리': 'implant-fixture, implant-abutment, implant-kit, implant-bone, implant-membrane, implant-accessory' },
            { '메인카테고리': 'restorative', '설명': '수복재료', '세부카테고리': 'restorative-composite, restorative-cement, restorative-adhesive, restorative-temporary' },
            { '메인카테고리': 'endodontic', '설명': '근관치료', '세부카테고리': 'endo-file, endo-sealer, endo-guttapercha, endo-irrigant' },
            { '메인카테고리': 'preventive', '설명': '예방치료', '세부카테고리': 'preventive-fluoride, preventive-sealant, preventive-polish' },
            { '메인카테고리': 'impression', '설명': '인상재료', '세부카테고리': 'impression-silicone, impression-alginate, impression-tray' },
            { '메인카테고리': 'orthodontic', '설명': '교정재료', '세부카테고리': 'ortho-bracket, ortho-wire, ortho-elastic, ortho-accessory' },
            { '메인카테고리': 'surgical', '설명': '외과용품', '세부카테고리': 'surgical-suture, surgical-blade, surgical-instrument' },
            { '메인카테고리': 'hygiene', '설명': '위생용품', '세부카테고리': 'hygiene-glove, hygiene-mask, hygiene-sterilization, hygiene-disposable' },
            { '메인카테고리': 'equipment', '설명': '장비', '세부카테고리': 'equip-handpiece, equip-light, equip-scaler, equip-xray, equip-other' },
            { '메인카테고리': 'lab-material', '설명': '기공재료', '세부카테고리': 'lab-zirconia, lab-porcelain, lab-wax, lab-alloy' },
            { '메인카테고리': 'other', '설명': '기타', '세부카테고리': '' }
        ]);
        XLSX.utils.book_append_sheet(wb, categorySheet, '카테고리 목록');

        // 단위 시트
        const unitSheet = XLSX.utils.json_to_sheet([
            { '단위코드': 'EA', '설명': '개' },
            { '단위코드': 'BOX', '설명': '박스' },
            { '단위코드': 'SET', '설명': '세트' },
            { '단위코드': 'PACK', '설명': '팩' },
            { '단위코드': 'KIT', '설명': '키트' },
            { '단위코드': 'BOTTLE', '설명': '병' },
            { '단위코드': 'TUBE', '설명': '튜브' },
            { '단위코드': 'SYRINGE', '설명': '시린지' },
            { '단위코드': 'KG', '설명': 'kg' },
            { '단위코드': 'G', '설명': 'g' },
            { '단위코드': 'ML', '설명': 'ml' },
            { '단위코드': 'L', '설명': 'L' }
        ]);
        XLSX.utils.book_append_sheet(wb, unitSheet, '단위 목록');

        // 사용안내 시트
        const guideSheet = XLSX.utils.json_to_sheet([
            { '단계': '1', '설명': '상품 정보를 입력하세요 (필수: 상품명, 카테고리, 판매가, 재고)' },
            { '단계': '2', '설명': '카테고리는 "카테고리 목록" 시트의 메인카테고리 값을 입력하세요' },
            { '단계': '3', '설명': '세부카테고리는 해당 메인카테고리의 세부카테고리 값을 입력하세요 (선택)' },
            { '단계': '4', '설명': '단위는 "단위 목록" 시트의 단위코드를 입력하세요 (예: EA, BOX, SET)' },
            { '단계': '5', '설명': '옵션은 최대 3그룹까지 설정 가능합니다 (옵션1~3)' },
            { '단계': '6', '설명': '옵션값과 추가금액의 개수는 동일해야 합니다 (예: 값 4개면 금액도 4개)' },
            { '단계': '7', '설명': '이미지 일괄 업로드 기능으로 이미지를 먼저 업로드하고 URL을 복사하세요' },
            { '단계': '8', '설명': '완성된 엑셀 파일을 "엑셀 업로드" 단계에서 업로드하세요' }
        ]);
        XLSX.utils.book_append_sheet(wb, guideSheet, '사용안내');

        XLSX.writeFile(wb, '치과재료_대량등록_템플릿_v4.xlsx');
    };

    // ── Step 2: 이미지 일괄 업로드 ──
    const handleMultipleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        try {
            setUploadingImages(true);
            const uploadPromises = files.map(async (file, index) => {
                const storageRef = ref(storage, `marketplace/${Date.now()}_${index}_${file.name}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                return { fileName: file.name, url };
            });

            const results = await Promise.all(uploadPromises);
            setUploadedImages(prev => [...prev, ...results]);
            alert(`${results.length}개 이미지 업로드 완료!`);
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다: ' + error.message);
        } finally {
            setUploadingImages(false);
        }
    };

    const downloadImageUrls = () => {
        if (uploadedImages.length === 0) {
            alert('업로드된 이미지가 없습니다.');
            return;
        }
        const data = uploadedImages.map((img, i) => ({
            '번호': i + 1,
            '파일명': img.fileName,
            '이미지URL': img.url
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 8 }, { wch: 30 }, { wch: 80 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '이미지 URL 목록');
        XLSX.writeFile(wb, '이미지_URL_목록.xlsx');
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('URL이 복사되었습니다!');
        }).catch(() => {
            // fallback
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            alert('URL이 복사되었습니다!');
        });
    };

    const removeImage = (index) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    // ── Step 3: 엑셀 파싱 ──
    const parseOptions = (row) => {
        const options = [];
        for (let g = 1; g <= 3; g++) {
            const nameKey = `옵션${g}명`;
            const valKey = `옵션${g}값`;
            const priceKey = `옵션${g}추가금액`;
            if (row[nameKey] && row[valKey]) {
                const values = row[valKey].split(',').map(v => v.trim()).filter(v => v);
                const prices = row[priceKey]
                    ? row[priceKey].split(',').map(p => parseInt(p.trim()) || 0)
                    : values.map(() => 0);
                if (values.length > 0) {
                    options.push({
                        name: row[nameKey].trim(),
                        values: values.map((v, i) => ({
                            value: v,
                            additionalPrice: prices[i] || 0
                        }))
                    });
                }
            }
        }
        return options;
    };

    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const validCategories = categories.map(c => c.value);

            const validatedData = jsonData.map((row, index) => {
                const errors = [];
                if (!row['상품명']) errors.push('상품명 필수');
                if (!row['카테고리']) errors.push('카테고리 필수');
                if (!row['판매가']) errors.push('판매가 필수');
                if (row['카테고리'] && !validCategories.includes(row['카테고리'])) {
                    errors.push('잘못된 카테고리');
                }
                for (let g = 1; g <= 3; g++) {
                    if (row[`옵션${g}명`] && !row[`옵션${g}값`]) {
                        errors.push(`옵션${g}값 필수`);
                    }
                }

                const imageUrls = [];
                for (let i = 1; i <= 5; i++) {
                    const urlKey = `이미지URL${i}`;
                    if (row[urlKey] && row[urlKey].toString().trim()) {
                        imageUrls.push(row[urlKey].toString().trim());
                    }
                }

                const options = parseOptions(row);

                return {
                    index: index + 1,
                    name: row['상품명'] || '',
                    brand: row['브랜드'] || '',
                    category: row['카테고리'] || 'other',
                    subCategory: row['세부카테고리'] || '',
                    description: row['상품설명'] || '',
                    price: parseInt(row['판매가']) || 0,
                    originalPrice: row['정가'] ? parseInt(row['정가']) : null,
                    discount: row['할인율'] ? parseInt(row['할인율']) : null,
                    stock: row['재고'] ? parseInt(row['재고']) : 0,
                    unit: row['단위'] || 'EA',
                    minOrder: row['최소주문수량'] ? parseInt(row['최소주문수량']) : 1,
                    specifications: row['제품사양'] || '',
                    features: row['주요특징'] || '',
                    imageUrls,
                    manufacturer: row['제조사'] || '',
                    origin: row['원산지'] || '',
                    certifications: row['인증정보'] || '',
                    shippingInfo: row['배송정보'] || '',
                    returnPolicy: row['반품정책'] || '',
                    options,
                    hasOptions: options.length > 0,
                    errors,
                    isValid: errors.length === 0
                };
            });

            setExcelData(validatedData);

            const invalidCount = validatedData.filter(d => !d.isValid).length;
            const optionCount = validatedData.filter(d => d.hasOptions).length;

            if (invalidCount > 0) {
                alert(`${validatedData.length}개 상품 중 ${invalidCount}개에 오류가 있습니다. 미리보기에서 확인하세요.`);
            } else {
                alert(`${validatedData.length}개 상품 확인 완료! (옵션 포함: ${optionCount}개)`);
            }
            setCurrentStep(4);
        } catch (error) {
            console.error('엑셀 파일 처리 실패:', error);
            alert('엑셀 파일을 읽는데 실패했습니다. 파일 형식을 확인해주세요.');
        }
    };

    // ── Step 4: 개별 행 삭제 ──
    const removeExcelRow = (index) => {
        setExcelData(prev => prev.filter((_, i) => i !== index));
    };

    const toggleRowExpand = (index) => {
        setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
    };

    // ── Step 5: 일괄 등록 ──
    const handleBulkUpload = async () => {
        const validProducts = excelData.filter(p => p.isValid);
        if (validProducts.length === 0) {
            alert('등록할 유효한 상품이 없습니다.');
            return;
        }

        const optionCount = validProducts.filter(p => p.hasOptions).length;
        if (!window.confirm(`${validProducts.length}개 상품을 등록하시겠습니까?\n(옵션 포함: ${optionCount}개)`)) {
            return;
        }

        try {
            setUploadingBulk(true);
            setUploadProgress(0);

            for (let i = 0; i < validProducts.length; i++) {
                const product = validProducts[i];
                const productData = {
                    name: product.name,
                    brand: product.brand,
                    category: product.category,
                    subCategory: product.subCategory,
                    description: product.description,
                    price: product.price,
                    originalPrice: product.originalPrice,
                    discount: product.discount,
                    stock: product.stock,
                    unit: product.unit,
                    minOrder: product.minOrder || 1,
                    specifications: product.specifications,
                    features: product.features,
                    manufacturer: product.manufacturer,
                    origin: product.origin,
                    certifications: product.certifications,
                    shippingInfo: product.shippingInfo,
                    returnPolicy: product.returnPolicy,
                    images: product.imageUrls || [],
                    imageUrls: product.imageUrls || [],
                    options: product.options || [],
                    hasOptions: product.hasOptions || false,
                    sellerId: userInfo.uid,
                    sellerName: userInfo.businessName || userInfo.name || userInfo.email,
                    status: 'active',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    viewCount: 0,
                    orderCount: 0
                };

                await addDoc(collection(db, 'marketplaceProducts'), productData);
                setUploadProgress(Math.round(((i + 1) / validProducts.length) * 100));
            }

            alert(`${validProducts.length}개 상품이 등록되었습니다!`);
            setExcelData([]);
            setCurrentStep(1);
            setUploadProgress(0);
        } catch (error) {
            console.error('일괄 업로드 실패:', error);
            alert('상품 등록 중 오류가 발생했습니다: ' + error.message);
        } finally {
            setUploadingBulk(false);
        }
    };

    // 카테고리 한글 변환
    const getCategoryLabel = (val) => {
        const found = categories.find(c => c.value === val);
        return found ? found.label : val;
    };

    const validCount = excelData.filter(p => p.isValid).length;
    const invalidCount = excelData.filter(p => !p.isValid).length;

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <button onClick={() => navigate('/product-management')} style={styles.backBtn}>
                        <ArrowLeft size={18} /> 상품관리
                    </button>
                    <h1 style={styles.title}>
                        <FileSpreadsheet size={28} style={{ color: '#6366f1' }} />
                        대량상품등록
                    </h1>
                </div>
            </div>

            {/* 단계 표시 */}
            <div style={styles.stepper}>
                {steps.map((step, i) => (
                    <div key={step.num} style={styles.stepWrapper}>
                        <div
                            onClick={() => {
                                if (step.num <= 3) setCurrentStep(step.num);
                                if (step.num === 4 && excelData.length > 0) setCurrentStep(4);
                                if (step.num === 5 && validCount > 0) setCurrentStep(5);
                            }}
                            style={{
                                ...styles.stepItem,
                                ...(currentStep === step.num ? styles.stepActive : {}),
                                ...(currentStep > step.num ? styles.stepDone : {}),
                                cursor: 'pointer'
                            }}
                        >
                            <div style={{
                                ...styles.stepNum,
                                ...(currentStep === step.num ? styles.stepNumActive : {}),
                                ...(currentStep > step.num ? styles.stepNumDone : {})
                            }}>
                                {currentStep > step.num ? <CheckCircle size={16} /> : step.num}
                            </div>
                            <div>
                                <div style={styles.stepTitle}>{step.title}</div>
                                <div style={styles.stepDesc}>{step.desc}</div>
                            </div>
                        </div>
                        {i < steps.length - 1 && <ChevronRight size={16} style={{ color: '#cbd5e1', flexShrink: 0 }} />}
                    </div>
                ))}
            </div>

            {/* Step 1: 템플릿 다운로드 */}
            {currentStep === 1 && (
                <div style={styles.stepCard}>
                    <h2 style={styles.stepCardTitle}>1. 엑셀 템플릿 다운로드</h2>
                    <p style={styles.stepCardDesc}>
                        아래 버튼을 클릭하여 상품 등록용 엑셀 템플릿을 다운로드하세요.
                        템플릿에는 예시 데이터, 카테고리 목록, 단위 목록, 사용안내가 포함되어 있습니다.
                    </p>

                    <div style={styles.featureList}>
                        <div style={styles.featureItem}>
                            <CheckCircle size={16} style={{ color: '#22c55e' }} />
                            <span>옵션 3그룹까지 지원 (예: 직경/길이/색상)</span>
                        </div>
                        <div style={styles.featureItem}>
                            <CheckCircle size={16} style={{ color: '#22c55e' }} />
                            <span>이미지 URL 5개까지 지원</span>
                        </div>
                        <div style={styles.featureItem}>
                            <CheckCircle size={16} style={{ color: '#22c55e' }} />
                            <span>치과 전문 카테고리 및 세부카테고리</span>
                        </div>
                    </div>

                    <button onClick={downloadExcelTemplate} style={styles.primaryBtn}>
                        <Download size={18} />
                        템플릿 다운로드 (.xlsx)
                    </button>

                    <button onClick={() => setCurrentStep(2)} style={styles.nextBtn}>
                        다음 단계로 <ChevronRight size={16} />
                    </button>
                </div>
            )}

            {/* Step 2: 이미지 업로드 */}
            {currentStep === 2 && (
                <div style={styles.stepCard}>
                    <h2 style={styles.stepCardTitle}>2. 이미지 일괄 업로드 (선택)</h2>
                    <p style={styles.stepCardDesc}>
                        상품 이미지를 먼저 업로드하면, URL을 복사하여 엑셀에 붙여넣을 수 있습니다.
                        이 단계는 건너뛸 수 있습니다.
                    </p>

                    <div style={styles.uploadArea}>
                        <label htmlFor="image-upload" style={styles.uploadLabel}>
                            <Images size={32} style={{ color: '#6366f1' }} />
                            <span style={{ fontWeight: 600 }}>이미지 선택</span>
                            <span style={{ fontSize: '13px', color: '#94a3b8' }}>JPG, PNG, WebP (여러 파일 선택 가능)</span>
                        </label>
                        <input
                            id="image-upload"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleMultipleImageUpload}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {uploadingImages && (
                        <div style={styles.loadingBar}>
                            <div style={styles.loadingBarInner} />
                            <span>이미지 업로드 중...</span>
                        </div>
                    )}

                    {uploadedImages.length > 0 && (
                        <div style={styles.imageListContainer}>
                            <div style={styles.imageListHeader}>
                                <span style={{ fontWeight: 600 }}>업로드된 이미지 ({uploadedImages.length}개)</span>
                                <button onClick={downloadImageUrls} style={styles.smallBtn}>
                                    <Download size={14} /> URL 목록 다운로드
                                </button>
                            </div>
                            <div style={styles.imageGrid}>
                                {uploadedImages.map((img, i) => (
                                    <div key={i} style={styles.imageItem}>
                                        <img src={img.url} alt={img.fileName} style={styles.imageThumb} />
                                        <div style={styles.imageInfo}>
                                            <span style={styles.imageName}>{img.fileName}</span>
                                            <div style={styles.imageActions}>
                                                <button onClick={() => copyToClipboard(img.url)} style={styles.iconBtn} title="URL 복사">
                                                    <Copy size={14} />
                                                </button>
                                                <button onClick={() => removeImage(i)} style={styles.iconBtnDanger} title="삭제">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={styles.btnRow}>
                        <button onClick={() => setCurrentStep(1)} style={styles.prevBtn}>
                            이전
                        </button>
                        <button onClick={() => setCurrentStep(3)} style={styles.nextBtn}>
                            {uploadedImages.length > 0 ? '다음 단계로' : '건너뛰기'} <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Step 3: 엑셀 업로드 */}
            {currentStep === 3 && (
                <div style={styles.stepCard}>
                    <h2 style={styles.stepCardTitle}>3. 엑셀 파일 업로드</h2>
                    <p style={styles.stepCardDesc}>
                        작성 완료된 엑셀 파일을 업로드하세요. 필수 항목(상품명, 카테고리, 판매가)을 확인합니다.
                    </p>

                    <div style={styles.uploadArea}>
                        <label htmlFor="excel-upload" style={styles.uploadLabel}>
                            <Upload size={32} style={{ color: '#6366f1' }} />
                            <span style={{ fontWeight: 600 }}>엑셀 파일 선택</span>
                            <span style={{ fontSize: '13px', color: '#94a3b8' }}>.xlsx, .xls 파일</span>
                        </label>
                        <input
                            id="excel-upload"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleExcelUpload}
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div style={styles.btnRow}>
                        <button onClick={() => setCurrentStep(2)} style={styles.prevBtn}>
                            이전
                        </button>
                    </div>
                </div>
            )}

            {/* Step 4: 미리보기/검증 */}
            {currentStep === 4 && (
                <div style={styles.stepCard}>
                    <h2 style={styles.stepCardTitle}>4. 미리보기 및 검증</h2>

                    <div style={styles.summaryBar}>
                        <div style={styles.summaryItem}>
                            <span style={{ color: '#64748b' }}>전체</span>
                            <strong>{excelData.length}개</strong>
                        </div>
                        <div style={styles.summaryItem}>
                            <CheckCircle size={16} style={{ color: '#22c55e' }} />
                            <strong style={{ color: '#22c55e' }}>{validCount}개 정상</strong>
                        </div>
                        {invalidCount > 0 && (
                            <div style={styles.summaryItem}>
                                <XCircle size={16} style={{ color: '#ef4444' }} />
                                <strong style={{ color: '#ef4444' }}>{invalidCount}개 오류</strong>
                            </div>
                        )}
                    </div>

                    <div style={styles.previewTable}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>상태</th>
                                    <th style={styles.th}>상품명</th>
                                    <th style={styles.th}>카테고리</th>
                                    <th style={styles.th}>판매가</th>
                                    <th style={styles.th}>재고</th>
                                    <th style={styles.th}>옵션</th>
                                    <th style={styles.th}>이미지</th>
                                    <th style={styles.th}>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {excelData.map((item, i) => (
                                    <React.Fragment key={i}>
                                        <tr style={{
                                            ...styles.tr,
                                            backgroundColor: !item.isValid ? '#fef2f2' : i % 2 === 0 ? '#fff' : '#f8fafc'
                                        }}>
                                            <td style={styles.td}>{item.index}</td>
                                            <td style={styles.td}>
                                                {item.isValid
                                                    ? <CheckCircle size={16} style={{ color: '#22c55e' }} />
                                                    : <div style={styles.errorBadge}>
                                                        <XCircle size={14} />
                                                        {item.errors.join(', ')}
                                                    </div>
                                                }
                                            </td>
                                            <td style={{ ...styles.td, fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.name}
                                            </td>
                                            <td style={styles.td}>{getCategoryLabel(item.category)}</td>
                                            <td style={styles.td}>{item.price?.toLocaleString()}원</td>
                                            <td style={styles.td}>{item.stock}</td>
                                            <td style={styles.td}>{item.options?.length || 0}개</td>
                                            <td style={styles.td}>{item.imageUrls?.length || 0}장</td>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button onClick={() => toggleRowExpand(i)} style={styles.iconBtn} title="상세보기">
                                                        {expandedRows[i] ? <ChevronDown size={14} /> : <Eye size={14} />}
                                                    </button>
                                                    <button onClick={() => removeExcelRow(i)} style={styles.iconBtnDanger} title="삭제">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRows[i] && (
                                            <tr>
                                                <td colSpan={9} style={styles.expandedCell}>
                                                    <div style={styles.expandedGrid}>
                                                        <div><strong>브랜드:</strong> {item.brand || '-'}</div>
                                                        <div><strong>제조사:</strong> {item.manufacturer || '-'}</div>
                                                        <div><strong>원산지:</strong> {item.origin || '-'}</div>
                                                        <div><strong>단위:</strong> {item.unit}</div>
                                                        <div><strong>최소주문:</strong> {item.minOrder}</div>
                                                        <div><strong>정가:</strong> {item.originalPrice?.toLocaleString() || '-'}원</div>
                                                        <div style={{ gridColumn: '1 / -1' }}><strong>설명:</strong> {item.description || '-'}</div>
                                                        <div style={{ gridColumn: '1 / -1' }}><strong>사양:</strong> {item.specifications || '-'}</div>
                                                        <div style={{ gridColumn: '1 / -1' }}><strong>특징:</strong> {item.features || '-'}</div>
                                                        {item.options?.length > 0 && (
                                                            <div style={{ gridColumn: '1 / -1' }}>
                                                                <strong>옵션:</strong>
                                                                {item.options.map((opt, oi) => (
                                                                    <span key={oi} style={styles.optionTag}>
                                                                        {opt.name}: {opt.values.map(v => `${v.value}(+${v.additionalPrice.toLocaleString()})`).join(', ')}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {item.imageUrls?.length > 0 && (
                                                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                                <strong>이미지:</strong>
                                                                {item.imageUrls.map((url, ui) => (
                                                                    <img key={ui} src={url} alt={`img-${ui}`} style={styles.previewThumb} />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={styles.btnRow}>
                        <button onClick={() => setCurrentStep(3)} style={styles.prevBtn}>
                            이전
                        </button>
                        <button
                            onClick={() => validCount > 0 && setCurrentStep(5)}
                            disabled={validCount === 0}
                            style={{
                                ...styles.nextBtn,
                                ...(validCount === 0 ? styles.disabledBtn : {})
                            }}
                        >
                            등록 진행 ({validCount}개) <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Step 5: 일괄 등록 */}
            {currentStep === 5 && (
                <div style={styles.stepCard}>
                    <h2 style={styles.stepCardTitle}>5. 일괄 등록</h2>

                    {!uploadingBulk ? (
                        <>
                            <div style={styles.finalSummary}>
                                <div style={styles.finalSummaryRow}>
                                    <span>등록 가능 상품</span>
                                    <strong style={{ color: '#22c55e', fontSize: 24 }}>{validCount}개</strong>
                                </div>
                                <div style={styles.finalSummaryRow}>
                                    <span>옵션 포함</span>
                                    <strong>{excelData.filter(p => p.hasOptions && p.isValid).length}개</strong>
                                </div>
                                <div style={styles.finalSummaryRow}>
                                    <span>이미지 포함</span>
                                    <strong>{excelData.filter(p => p.imageUrls?.length > 0 && p.isValid).length}개</strong>
                                </div>
                                {invalidCount > 0 && (
                                    <div style={styles.finalSummaryRow}>
                                        <span style={{ color: '#ef4444' }}>오류 (제외)</span>
                                        <strong style={{ color: '#ef4444' }}>{invalidCount}개</strong>
                                    </div>
                                )}
                            </div>

                            {invalidCount > 0 && (
                                <div style={styles.warningBox}>
                                    <AlertTriangle size={16} />
                                    <span>오류가 있는 {invalidCount}개 상품은 등록에서 제외됩니다.</span>
                                </div>
                            )}

                            <div style={styles.btnRow}>
                                <button onClick={() => setCurrentStep(4)} style={styles.prevBtn}>
                                    이전
                                </button>
                                <button onClick={handleBulkUpload} style={styles.registerBtn}>
                                    <Upload size={18} />
                                    {validCount}개 상품 일괄 등록
                                </button>
                            </div>
                        </>
                    ) : (
                        <div style={styles.progressContainer}>
                            <div style={styles.progressBarOuter}>
                                <div style={{ ...styles.progressBarInner, width: `${uploadProgress}%` }} />
                            </div>
                            <p style={styles.progressText}>
                                등록 중... {uploadProgress}%
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: '24px 16px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    backBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 12px',
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        fontSize: 13,
        color: '#64748b',
        cursor: 'pointer',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 24,
        fontWeight: 700,
        color: '#1e293b',
        margin: 0,
    },
    stepper: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '16px 20px',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        marginBottom: 24,
        overflowX: 'auto',
    },
    stepWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flex: 1,
        minWidth: 0,
    },
    stepItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 8,
        flex: 1,
        minWidth: 0,
        transition: 'all 0.2s',
    },
    stepActive: {
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
    },
    stepDone: {
        background: '#f0fdf4',
    },
    stepNum: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: '#e2e8f0',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
    },
    stepNumActive: {
        background: '#6366f1',
        color: '#fff',
    },
    stepNumDone: {
        background: '#22c55e',
        color: '#fff',
    },
    stepTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: '#1e293b',
        whiteSpace: 'nowrap',
    },
    stepDesc: {
        fontSize: 11,
        color: '#94a3b8',
        whiteSpace: 'nowrap',
    },
    stepCard: {
        background: '#fff',
        borderRadius: 12,
        padding: 28,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    },
    stepCardTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: '#1e293b',
        marginBottom: 8,
    },
    stepCardDesc: {
        fontSize: 14,
        color: '#64748b',
        marginBottom: 24,
        lineHeight: 1.6,
    },
    featureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginBottom: 24,
        padding: 16,
        background: '#f8fafc',
        borderRadius: 8,
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: '#475569',
    },
    primaryBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 24px',
        background: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    },
    nextBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 20px',
        background: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        marginLeft: 12,
    },
    prevBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 20px',
        background: '#f1f5f9',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },
    btnRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 24,
    },
    uploadArea: {
        border: '2px dashed #d1d5db',
        borderRadius: 12,
        padding: 32,
        textAlign: 'center',
        marginBottom: 16,
        transition: 'border-color 0.2s',
    },
    uploadLabel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        color: '#475569',
    },
    loadingBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: '#eef2ff',
        borderRadius: 8,
        marginBottom: 16,
        fontSize: 14,
        color: '#6366f1',
    },
    loadingBarInner: {
        width: 20,
        height: 20,
        border: '3px solid #c7d2fe',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    imageListContainer: {
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: 16,
        marginBottom: 16,
    },
    imageListHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    smallBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 12px',
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        fontSize: 12,
        color: '#475569',
        cursor: 'pointer',
    },
    imageGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 12,
    },
    imageItem: {
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
    },
    imageThumb: {
        width: '100%',
        height: 120,
        objectFit: 'cover',
    },
    imageInfo: {
        padding: '8px 10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    imageName: {
        fontSize: 12,
        color: '#64748b',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 100,
    },
    imageActions: {
        display: 'flex',
        gap: 4,
    },
    iconBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        cursor: 'pointer',
        color: '#64748b',
    },
    iconBtnDanger: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 6,
        cursor: 'pointer',
        color: '#ef4444',
    },
    summaryBar: {
        display: 'flex',
        gap: 24,
        padding: '12px 20px',
        background: '#f8fafc',
        borderRadius: 8,
        marginBottom: 16,
    },
    summaryItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 14,
    },
    previewTable: {
        overflowX: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
    },
    th: {
        padding: '10px 12px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        textAlign: 'left',
        fontWeight: 600,
        color: '#475569',
        whiteSpace: 'nowrap',
    },
    tr: {
        borderBottom: '1px solid #f1f5f9',
    },
    td: {
        padding: '10px 12px',
        verticalAlign: 'middle',
    },
    errorBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        background: '#fef2f2',
        color: '#ef4444',
        borderRadius: 4,
        fontSize: 12,
    },
    expandedCell: {
        padding: '12px 16px',
        background: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
    },
    expandedGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '8px 16px',
        fontSize: 13,
        color: '#475569',
    },
    optionTag: {
        display: 'inline-block',
        padding: '2px 8px',
        background: '#eef2ff',
        color: '#6366f1',
        borderRadius: 4,
        fontSize: 12,
        marginLeft: 6,
        marginTop: 4,
    },
    previewThumb: {
        width: 48,
        height: 48,
        objectFit: 'cover',
        borderRadius: 4,
        border: '1px solid #e2e8f0',
    },
    disabledBtn: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    finalSummary: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 16,
        padding: 20,
        background: '#f8fafc',
        borderRadius: 10,
        marginBottom: 20,
    },
    finalSummaryRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: 12,
        background: '#fff',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        fontSize: 14,
        color: '#64748b',
    },
    warningBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 16px',
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 8,
        color: '#92400e',
        fontSize: 14,
    },
    registerBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '14px 32px',
        background: '#22c55e',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontSize: 16,
        fontWeight: 700,
        cursor: 'pointer',
    },
    progressContainer: {
        textAlign: 'center',
        padding: '40px 0',
    },
    progressBarOuter: {
        width: '100%',
        height: 12,
        background: '#e2e8f0',
        borderRadius: 6,
        overflow: 'hidden',
        marginBottom: 16,
    },
    progressBarInner: {
        height: '100%',
        background: 'linear-gradient(90deg, #6366f1, #22c55e)',
        borderRadius: 6,
        transition: 'width 0.3s ease',
    },
    progressText: {
        fontSize: 16,
        fontWeight: 600,
        color: '#6366f1',
    },
};

export default BulkProductUpload;
